<script>
  import { ChevronLeft, ChevronRight, Download, RotateCw, Share2, X, ZoomIn, ZoomOut } from 'lucide-svelte';
  import { onDestroy, onMount } from 'svelte';
  import Portal from 'svelte-portal';

  export let images = [];
  export let index = 0;
  export let onClose = () => {};

  let scale = 1;
  let translateX = 0;
  let translateY = 0;
  let rotation = 0;
  let isDragging = false;
  let startX = 0;
  let startY = 0;
  let thumbContainer;

  $: currentImage = images[index] || '';
  $: if (thumbContainer) {
    const selected = thumbContainer.querySelector(`[data-thumb='${index}']`);
    selected?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }

  function resetTransform() {
    scale = 1;
    translateX = 0;
    translateY = 0;
    rotation = 0;
  }

  function next() {
    if (index >= images.length - 1) return;
    index += 1;
    resetTransform();
  }

  function prev() {
    if (index <= 0) return;
    index -= 1;
    resetTransform();
  }

  function zoomIn() {
    scale = Math.min(4, scale + 0.35);
  }

  function zoomOut() {
    scale = Math.max(1, scale - 0.35);
    if (scale === 1) resetTransform();
  }

  function wheelZoom(event) {
    event.preventDefault();
    scale = Math.min(4, Math.max(1, scale + event.deltaY * -0.002));
  }

  function dragStart(event) {
    const point = event.touches ? event.touches[0] : event;
    isDragging = true;
    startX = point.clientX - translateX;
    startY = point.clientY - translateY;
  }

  function dragMove(event) {
    if (!isDragging || scale <= 1) return;
    const point = event.touches ? event.touches[0] : event;
    translateX = point.clientX - startX;
    translateY = point.clientY - startY;
  }

  function dragEnd() {
    isDragging = false;
  }

  function rotateRight() {
    rotation = (rotation + 90) % 360;
  }

  function handleKey(event) {
    if (event.key === 'Escape') onClose?.();
    if (event.key === 'ArrowRight') next();
    if (event.key === 'ArrowLeft') prev();
  }

  function downloadNow() {
    if (!currentImage) return;

    const fileName = currentImage.split('/').pop()?.split('?')[0] || `image-${index + 1}`;

    fetch(currentImage, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error('download_failed');
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName;
        link.rel = 'noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {
        const link = document.createElement('a');
        link.href = currentImage;
        link.target = '_blank';
        link.rel = 'noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
  }

  async function shareNow() {
    if (!currentImage) return;

    try {
      const fileName = currentImage.split('/').pop()?.split('?')[0] || `image-${index + 1}`;
      const response = await fetch(currentImage, { credentials: 'include' });
      const blob = response.ok ? await response.blob() : null;

      if (blob) {
        const file = new File([blob], fileName, {
          type: blob.type || 'image/jpeg'
        });

        if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
          await navigator.share({
            title: 'Chia sẻ ảnh',
            text: fileName,
            files: [file]
          });
          return;
        }
      }

      if (navigator.share) {
        await navigator.share({
          title: 'Chia sẻ ảnh',
          text: fileName,
          url: currentImage
        });
        return;
      }

      await navigator.clipboard?.writeText(currentImage);
    } catch (error) {
      console.warn('Share image failed:', error);
      try {
        await navigator.clipboard?.writeText(currentImage);
      } catch {
        // no-op
      }
    }
  }

  onMount(() => {
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
  });

  onDestroy(() => {
    document.body.style.overflow = '';
    window.removeEventListener('keydown', handleKey);
  });
</script>

<Portal>
  <div class="gallery-overlay" on:click={() => onClose()}>
    <section
      class="gallery-card"
      on:click|stopPropagation
      on:wheel={wheelZoom}
      on:mousedown={dragStart}
      on:mousemove={dragMove}
      on:mouseup={dragEnd}
      on:mouseleave={dragEnd}
      on:touchstart={dragStart}
      on:touchmove={dragMove}
      on:touchend={dragEnd}>
      <header class="gallery-header">
        <div class="title-block">
          <span class="badge">Ảnh</span>
          <strong>{index + 1} / {images.length}</strong>
        </div>

        <div class="actions">
          <button type="button" title="Thu nhỏ" on:click={zoomOut}><ZoomOut size={16} /></button>
          <button type="button" title="Phóng to" on:click={zoomIn}><ZoomIn size={16} /></button>
          <button type="button" title="Xoay ảnh" on:click={rotateRight}><RotateCw size={16} /></button>
          <button type="button" title="Chia sẻ ảnh" on:click={shareNow}><Share2 size={16} /></button>
          <button type="button" title="Tải ảnh" on:click={downloadNow}><Download size={16} /></button>
          <button type="button" title="Đóng" on:click={() => onClose()}><X size={16} /></button>
        </div>
      </header>

      <main class="gallery-stage">
        {#if index > 0}
          <button class="nav-button left" type="button" on:click={prev}><ChevronLeft size={20} /></button>
        {/if}

        <img
          src={currentImage}
          alt={`Image ${index + 1}`}
          class="main-image"
          style="transform: translate({translateX}px, {translateY}px) rotate({rotation}deg) scale({scale});"
          draggable="false" />

        {#if index < images.length - 1}
          <button class="nav-button right" type="button" on:click={next}><ChevronRight size={20} /></button>
        {/if}
      </main>

      {#if images.length > 1}
        <footer bind:this={thumbContainer} class="thumbnail-strip">
          {#each images as image, i}
            <button
              type="button"
              data-thumb={i}
              class:active={index === i}
              class="thumb-button"
              on:click={() => {
                index = i;
                resetTransform();
              }}>
              <img src={image} alt={`Thumbnail ${i + 1}`} />
            </button>
          {/each}
        </footer>
      {/if}
    </section>
  </div>
</Portal>

<style>
  .gallery-overlay {
    position: fixed;
    inset: 0;
    z-index: 2147483000;
    display: grid;
    place-items: center;
    padding: 0.8rem;
    background: rgba(2, 6, 23, 0.72);
    backdrop-filter: blur(14px);
  }

  .gallery-card {
    display: flex;
    flex-direction: column;
    width: min(96vw, 80rem);
    height: min(94dvh, 94vh);
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 1.6rem;
    background:
      linear-gradient(180deg, rgba(15, 23, 42, 0.48), rgba(2, 6, 23, 0.76)),
      radial-gradient(circle at top right, rgba(14, 165, 233, 0.18), transparent 16rem);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.14),
      0 30px 80px rgba(0, 0, 0, 0.32);
    backdrop-filter: blur(28px);
  }

  .gallery-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.8rem;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.06);
    backdrop-filter: blur(18px);
  }

  .badge {
    display: inline-block;
    padding: 0.16rem 0.45rem;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 999px;
    color: #cffafe;
    background: rgba(8, 145, 178, 0.16);
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .title-block strong {
    display: block;
    margin-top: 0.3rem;
    color: white;
    font-size: 0.88rem;
    font-weight: 700;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .actions button,
  .nav-button {
    display: grid;
    place-items: center;
    width: 2.2rem;
    height: 2.2rem;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 999px;
    color: #e2e8f0;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(16px);
  }

  .gallery-stage {
    position: relative;
    flex: 1;
    min-height: 0;
    display: grid;
    place-items: center;
    overflow: hidden;
    padding: 1rem;
  }

  .main-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    image-rendering: auto;
    transition: transform 120ms ease-out;
    user-select: none;
  }

  .nav-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 2.6rem;
    height: 2.6rem;
  }

  .nav-button.left {
    left: 1rem;
  }
  .nav-button.right {
    right: 1rem;
  }

  .thumbnail-strip {
    display: flex;
    gap: 0.55rem;
    overflow-x: auto;
    padding: 0.8rem 1rem max(0.8rem, env(safe-area-inset-bottom));
    background: rgba(255, 255, 255, 0.06);
    backdrop-filter: blur(20px);
  }

  .thumb-button {
    flex: 0 0 auto;
    width: 4.2rem;
    height: 4.2rem;
    overflow: hidden;
    border: 1px solid transparent;
    border-radius: 0.95rem;
    opacity: 0.62;
  }

  .thumb-button.active {
    border-color: rgba(125, 211, 252, 0.9);
    opacity: 1;
    box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.25);
  }

  .thumb-button img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 720px) {
    .gallery-overlay {
      padding: 0;
    }
    .gallery-card {
      width: 100vw;
      height: 100dvh;
      border-radius: 0;
    }
    .thumb-button {
      width: 3.6rem;
      height: 3.6rem;
    }
  }
</style>
